import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Sparkles, Eye, EyeOff, CheckCircle2, AlertCircle } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("Kein Reset-Token im Link gefunden.");
  }, [token, setError]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    if (password !== confirm) {
      setError("Passwörter stimmen nicht überein.");
      return;
    }
    setBusy(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/auth/password-reset/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Zurücksetzen fehlgeschlagen.");
      }
      setDone(true);
      setTimeout(() => navigate("/", { replace: true }), 3500);
    } catch (err) {
      setError(err.message || "Zurücksetzen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white antialiased flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#00e5ff]/[0.06] blur-3xl" />
      </div>

      <div className="relative max-w-md w-full" data-testid="reset-password-container">
        <div className="bg-[#0a0a0a] border border-white/10 p-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-6 h-px bg-[#00e5ff]/60" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#00e5ff]/70 font-outfit font-semibold">
              Passwort zurücksetzen
            </span>
          </div>

          {done ? (
            <div data-testid="reset-done">
              <h1 className="font-outfit font-black text-3xl text-white tracking-tighter mb-3">
                <span className="text-[#00e5ff]">Erledigt.</span>
              </h1>
              <div className="flex items-start gap-3 bg-[#00e5ff]/5 border border-[#00e5ff]/30 p-4 mb-4">
                <CheckCircle2 size={18} className="text-[#00e5ff] shrink-0 mt-0.5" />
                <p className="text-sm text-white/80 leading-relaxed">
                  Dein Passwort wurde geändert. Du wirst in Kürze zur Startseite weitergeleitet.
                </p>
              </div>
              <Link
                to="/"
                className="block text-center text-sm text-white/50 hover:text-[#00e5ff]/80 transition-colors"
              >
                Zur Anmeldung →
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-outfit font-black text-3xl text-white tracking-tighter mb-3">
                Neues <span className="text-[#00e5ff]">Passwort</span> setzen
              </h1>
              <p className="text-sm text-white/50 leading-relaxed mb-6">
                Wähle ein neues Passwort (mindestens 8 Zeichen). Anschließend kannst du dich damit anmelden.
              </p>

              {error && (
                <div className="flex items-start gap-2 text-red-400 text-sm bg-red-400/10 border border-red-400/30 px-3 py-2.5 mb-4" data-testid="reset-error">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-outfit">
                    Neues Passwort
                  </Label>
                  <div className="relative mt-1.5">
                    <Input
                      type={show ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="reset-password-input"
                      className="bg-black border-white/10 text-white rounded-none h-11 pr-10 focus:border-[#00e5ff]/60 focus:ring-0"
                    />
                    <button
                      type="button"
                      onClick={() => setShow(!show)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-[#00e5ff]/80"
                    >
                      {show ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-outfit">
                    Passwort bestätigen
                  </Label>
                  <Input
                    type={show ? "text" : "password"}
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-password-confirm"
                    className="mt-1.5 bg-black border-white/10 text-white rounded-none h-11 focus:border-[#00e5ff]/60 focus:ring-0"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={busy || !token}
                  data-testid="reset-submit"
                  className="w-full bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] rounded-none font-outfit font-semibold h-11 text-sm tracking-wide transition-all"
                >
                  <Sparkles size={14} className="mr-2" />
                  {busy ? "…" : "Passwort ändern"}
                </Button>
              </form>

              <Link
                to="/"
                className="block mt-4 text-center text-sm text-white/40 hover:text-[#00e5ff]/80 transition-colors"
              >
                ← Zurück zur Anmeldung
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
